import { useEffect, useMemo, useState } from 'react';
import { formatDateKey, todayKey } from '../../core/dates';
import {
  MAHINDRA_CAR_LOAN,
  instalmentsDueBy,
  outstandingPrincipalAt,
  quote,
  scheduleTotals,
  type ChargeKind,
  type Instalment,
  type Loan,
} from '../../core/loans';
import { formatPaise, parseAmount } from '../../core/money';
import type { UserRecord } from '../../data/auth';
import {
  paidInstalments,
  seedLoan,
  uploadLoanDocument,
  watchLoans,
  watchSchedule,
  type LoanDocument,
  type PaidInstalment,
} from '../../data/loans';

interface Props {
  record: UserRecord;
}

/**
 * Loans (FR-LOAN-01 to FR-LOAN-11).
 *
 * Three panels: the loan's terms, the foreclosure calculator, and the schedule with paid
 * instalments marked. Nothing on this screen is specific to the one seeded loan — a
 * second loan renders identically (FR-LOAN-10).
 */
export function LoansScreen({ record }: Props) {
  const [loans, setLoans] = useState<Loan[] | null>(null);
  const [schedule, setSchedule] = useState<Instalment[]>([]);
  const [paid, setPaid] = useState<PaidInstalment[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const [asAt, setAsAt] = useState(todayKey());
  const [kind, setKind] = useState<ChargeKind>('foreclosure');
  const [partPayment, setPartPayment] = useState('');
  const [showAll, setShowAll] = useState(false);

  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => watchLoans(setLoans), []);

  const loan = useMemo(
    () => loans?.find((l) => l.id === selectedId) ?? loans?.[0] ?? null,
    [loans, selectedId],
  );

  useEffect(() => {
    if (!loan) return;
    return watchSchedule(loan.id, setSchedule);
  }, [loan?.id]);

  useEffect(() => {
    if (!loan) return;
    let live = true;
    void paidInstalments(loan.id).then((rows) => {
      if (live) setPaid(rows);
    });
    return () => {
      live = false;
    };
  }, [loan?.id, schedule.length]);

  const paidNumbers = useMemo(() => new Set(paid.map((p) => p.instalmentNo)), [paid]);

  const result = useMemo(() => {
    if (!loan || schedule.length === 0) return null;
    try {
      return quote({
        loan,
        schedule,
        asAt,
        kind,
        partPaymentPaise:
          kind === 'part-payment' ? (parseAmount(partPayment) ?? 0) : undefined,
      });
    } catch (cause) {
      return cause instanceof Error ? cause.message : 'Could not build a quote.';
    }
  }, [loan, schedule, asAt, kind, partPayment]);

  async function seed() {
    setError(null);
    setBusy('seed');
    try {
      const count = await seedLoan(record.uid);
      setNotice(`Seeded the loan and ${count} instalments from the lender's schedule.`);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not seed the loan.');
    } finally {
      setBusy(null);
    }
  }

  async function upload(file: File, kindOfDoc: LoanDocument['kind']) {
    if (!loan) return;
    setError(null);
    setBusy('upload');
    try {
      const document = await uploadLoanDocument(loan.id, file, kindOfDoc);
      setNotice(`Stored ${document.name}. It cannot be opened in the app until the signed-URL route arrives at M9.`);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not upload that.');
    } finally {
      setBusy(null);
    }
  }

  if (loans === null) return <p className="muted">Loading…</p>;

  if (loans.length === 0) {
    return (
      <>
        <div className="head-row">
          <h1>Loans</h1>
        </div>
        {error && <p className="error">{error}</p>}
        <div className="placeholder">
          <p>
            No loan record yet. Seeding writes <strong>{MAHINDRA_CAR_LOAN.name}</strong> and all
            60 instalments from the lender's repayment schedule.
          </p>
          <button type="button" className="btn btn-primary" disabled={busy !== null} onClick={() => void seed()}>
            {busy === 'seed' ? 'Seeding…' : 'Seed the loan'}
          </button>
        </div>
      </>
    );
  }

  if (!loan) return <p className="muted">Loading…</p>;

  const totals = scheduleTotals(schedule);
  const due = instalmentsDueBy(schedule, asAt);
  const visible = showAll ? schedule : schedule.slice(0, Math.max(due.length + 6, 12));

  return (
    <>
      <div className="head-row">
        <h1>{loan.name}</h1>
        {loans.length > 1 && (
          <select value={loan.id} onChange={(e) => setSelectedId(e.target.value)} aria-label="Loan">
            {loans.map((l) => (
              <option key={l.id} value={l.id}>{l.name}</option>
            ))}
          </select>
        )}
      </div>

      {error && <p className="error">{error}</p>}
      {notice && <p className="ok">{notice}</p>}

      <div className="figures">
        <div className="figure">
          <div className="figure-label">Outstanding principal</div>
          <div className="figure-value">
            {formatPaise(outstandingPrincipalAt(loan, schedule, asAt))}
          </div>
          <div className="muted tiny">
            after {due.length} of {loan.tenureMonths} instalments
          </div>
        </div>
        <div className="figure">
          <div className="figure-label">EMI</div>
          <div className="figure-value">{formatPaise(loan.emiPaise)}</div>
          <div className="muted tiny">on the {loan.emiDayOfMonth}th · {loan.repaymentMode}</div>
        </div>
        <div className="figure">
          <div className="figure-label">Interest over the term</div>
          <div className="figure-value">{formatPaise(totals.interestPaise)}</div>
          <div className="muted tiny">{loan.interestRatePercent}% fixed · APR {loan.aprExclInsurancePercent}%</div>
        </div>
      </div>

      <div className="panel-grid">
        {/* ---- Foreclosure calculator (FR-LOAN-07) ---- */}
        <section className="panel">
          <h2>Settle early</h2>

          <div className="row">
            <label className="inline">
              As at
              <input type="date" value={asAt} onChange={(e) => setAsAt(e.target.value)} />
            </label>
            <select
              value={kind}
              onChange={(e) => setKind(e.target.value as ChargeKind)}
              aria-label="Charge type"
            >
              <option value="foreclosure">Foreclose in full</option>
              <option value="part-payment">Part-payment</option>
            </select>
            {kind === 'part-payment' && (
              <input
                type="text"
                inputMode="decimal"
                value={partPayment}
                onChange={(e) => setPartPayment(e.target.value)}
                placeholder="Amount"
                aria-label="Part-payment amount"
              />
            )}
          </div>

          {typeof result === 'string' ? (
            <p className="error tiny">{result}</p>
          ) : result ? (
            <table className="table compact quote">
              <tbody>
                <tr>
                  <td>{kind === 'part-payment' ? 'Part-payment' : 'Outstanding principal'}</td>
                  <td className="num">{formatPaise(result.chargeBasePaise)}</td>
                </tr>
                <tr>
                  <td>
                    Charge at {result.ratePercent}%
                    {/* The rule is a cliff worth three percentage points, so the reason
                        for the rate sits next to it rather than in a help page. */}
                    <div className="muted tiny">{result.rateReason}</div>
                  </td>
                  <td className="num">{formatPaise(result.chargePaise)}</td>
                </tr>
                {result.penalPaise > 0 && (
                  <tr>
                    <td>Penal charge</td>
                    <td className="num">{formatPaise(result.penalPaise)}</td>
                  </tr>
                )}
                {result.bouncePaise > 0 && (
                  <tr>
                    <td>Bounce charges</td>
                    <td className="num">{formatPaise(result.bouncePaise)}</td>
                  </tr>
                )}
                <tr>
                  <td>
                    GST at 18%
                    <div className="muted tiny">on the charge, never on the principal</div>
                  </td>
                  <td className="num">{formatPaise(result.gstPaise)}</td>
                </tr>
                <tr className="total-row">
                  <td><strong>Payoff</strong></td>
                  <td className="num"><strong>{formatPaise(result.payoffPaise)}</strong></td>
                </tr>
              </tbody>
            </table>
          ) : (
            <p className="muted tiny">Loading the schedule…</p>
          )}
        </section>

        {/* ---- Terms (FR-LOAN-03) ---- */}
        <section className="panel">
          <h2>Terms</h2>
          <table className="table compact">
            <tbody>
              <tr><td>Lender</td><td>{loan.lender}</td></tr>
              <tr><td>Account</td><td>{loan.loanAccountNumber}</td></tr>
              <tr><td>Asset</td><td>{loan.asset}</td></tr>
              <tr>
                <td>Sanctioned</td>
                <td>
                  {formatPaise(loan.sanctionedPaise)}
                  <div className="muted tiny">
                    incl. {loan.addOns.map((a) => `${a.label} ${formatPaise(a.amountPaise, { symbol: false })}`).join(' + ')}
                  </div>
                </td>
              </tr>
              <tr><td>Disbursed</td><td>{formatDateKey(loan.disbursedOn, 'dd MMM yyyy')}</td></tr>
              <tr><td>Agreement</td><td>{formatDateKey(loan.agreementDate, 'dd MMM yyyy')}</td></tr>
              <tr><td>Total payable</td><td>{formatPaise(loan.totalPayablePaise)}</td></tr>
              <tr>
                <td>One-time fees</td>
                <td>
                  {formatPaise(loan.oneTimeFees.reduce((s, f) => s + f.amountPaise, 0))}
                  <div className="muted tiny">
                    {loan.oneTimeFees.map((f) => `${f.label} ${f.amountPaise / 100}`).join(' · ')}
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </section>

        {/* ---- Documents (FR-LOAN-09, AD-10) ---- */}
        <section className="panel">
          <h2>Documents</h2>
          <p className="muted tiny">
            Held under stricter rules than receipts: they carry PAN, Aadhaar references,
            date of birth, address and income. <strong>No client can read them directly</strong> —
            opening one needs the signed-URL route, which arrives with the scheduled jobs at M9.
          </p>
          <div className="row">
            <label className="btn">
              Repayment schedule
              <input
                type="file"
                accept="application/pdf,image/*"
                hidden
                onChange={(e) => e.target.files?.[0] && void upload(e.target.files[0], 'schedule')}
              />
            </label>
            <label className="btn">
              KFS
              <input
                type="file"
                accept="application/pdf,image/*"
                hidden
                onChange={(e) => e.target.files?.[0] && void upload(e.target.files[0], 'kfs')}
              />
            </label>
          </div>
          {busy === 'upload' && <p className="muted tiny">Uploading…</p>}
        </section>
      </div>

      {/* ---- The schedule (FR-LOAN-05) ---- */}
      <div className="head-row" style={{ marginTop: '1.25rem' }}>
        <h2>Repayment schedule</h2>
        <span className="muted tiny">
          {paid.length} posted · from the lender's PDF, never recomputed
        </span>
      </div>

      <div className="table-scroll">
        <table className="table reclassify-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Due</th>
              <th className="num">Instalment</th>
              <th className="num">Principal</th>
              <th className="num">Interest</th>
              <th className="num">Outstanding</th>
              <th>Posted</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((instalment) => (
              <tr key={instalment.instalmentNo}>
                <td>{instalment.instalmentNo}</td>
                <td>{formatDateKey(instalment.dueDateKey, 'dd MMM yyyy')}</td>
                <td className="num">{formatPaise(instalment.amountPaise, { symbol: false })}</td>
                <td className="num">{formatPaise(instalment.principalPaise, { symbol: false })}</td>
                <td className="num">{formatPaise(instalment.interestPaise, { symbol: false })}</td>
                <td className="num">{formatPaise(instalment.outstandingPaise, { symbol: false })}</td>
                <td>
                  {paidNumbers.has(instalment.instalmentNo) ? (
                    <span className="chip tiny">posted</span>
                  ) : instalment.dueDateKey <= asAt ? (
                    <span className="muted tiny">due, not posted</span>
                  ) : (
                    <span className="muted tiny">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {!showAll && schedule.length > visible.length && (
        <button type="button" className="btn-link" onClick={() => setShowAll(true)}>
          Show all {schedule.length} instalments
        </button>
      )}
    </>
  );
}
